import React, { useState, useCallback, useEffect, useContext } from "react";
import { X, Calendar } from "lucide-react";
import { useView } from "../propertiesPage/context/ViewContext";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Context } from "services";

interface RangeDateSearchProps {
  onValueChange: (value: string | null) => void;
}

const RangeDateSearch: React.FC<RangeDateSearchProps> = ({ onValueChange }) => {
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const { clearRangeDateSearch, setClearRangeDateSearch } = useView();

  const dateToUnixTimestamp = (date: Date | undefined): number | null => {
    return date ? Math.floor(date.getTime()) : null;
  };

  const validateAndPrepareSearch = useCallback(() => {
    if (startDate && endDate && startDate > endDate) {
      setErrorMessage("Start date cannot be later than End date");
      return null;
    } else {
      setErrorMessage("");
    }
    const startTimestamp = dateToUnixTimestamp(startDate);
    const endTimestamp = dateToUnixTimestamp(endDate);
    if (startTimestamp && endTimestamp) {
      return `${startTimestamp}-${endTimestamp}`;
    }
    return null;
  }, [startDate, endDate]);

  useEffect(() => {
    const value = validateAndPrepareSearch();
    onValueChange(value);
  }, [startDate, endDate, validateAndPrepareSearch, onValueChange]);

  useEffect(() => {
    if (clearRangeDateSearch) {
      handleReset();
      setClearRangeDateSearch(false);
    }
  }, [clearRangeDateSearch, setClearRangeDateSearch]);

  const handleStartDateChange = (date: Date | null) => {
    setStartDate(date || undefined);
    if (endDate && date && date > endDate) {
      setEndDate(undefined);
    }
  };

  const handleEndDateChange = (date: Date | null) => {
    if (!startDate || (date && date >= startDate)) {
      setEndDate(date || undefined);
      setErrorMessage("");
    } else {
      setErrorMessage("End date cannot be earlier than Start date");
    }
  };

  const handleReset = () => {
    if (searchForm && searchForm.setField) {
      searchForm.setField("auction_start_date_unix_timestamp", "");
    }
    setStartDate(undefined);
    setEndDate(undefined);
    setErrorMessage("");
    onValueChange(null);
  };

  const showResetIcon = startDate || endDate;

  const CustomInput = React.forwardRef<HTMLInputElement, { value?: string; onClick?: () => void; disabled?: boolean }>(
    ({ value, onClick, disabled }, ref) => (
      <div 
        className="w-full relative cursor-pointer" 
        onClick={disabled ? undefined : onClick}
      >
        <input
          value={value}
          readOnly
          ref={ref}
          disabled={disabled}
          className={`w-full px-3 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            disabled ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : 'bg-white cursor-pointer'
          }`}
          placeholder="DD/MM/YYYY"
        />
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
          <Calendar className="text-gray-400" size={18} />
        </div>
      </div>
    )
  );

  const { searchForm }: { searchForm?: { setField?: (key: string, value: string) => void } } = useContext(Context);

  return (
    <div className="py-2 px-1 flex flex-col gap-y-1">
      <div className="flex justify-between items-center">
        <h3 className="text-sm font-medium">Select Date Range</h3>
        {showResetIcon && (
          <button onClick={handleReset} className="text-gray-500 hover:text-gray-700">
            <X size={16} />
          </button>
        )}
      </div>
      <div className="flex flex-col gap-y-2">
        <div>
          <div className="text-xs font-medium text-gray-600 mb-1">From</div>
          <DatePicker
            selected={startDate}
            onChange={handleStartDateChange}
            selectsStart
            startDate={startDate}
            endDate={endDate}
            minDate={new Date()}
            customInput={<CustomInput />}
            dateFormat="dd/MM/yyyy"
          />
        </div>
        <div>
          <div className="text-xs font-medium text-gray-600 mb-1">To</div>
          <DatePicker
            selected={endDate}
            onChange={handleEndDateChange}
            selectsEnd
            startDate={startDate}
            endDate={endDate}
            minDate={startDate || new Date()}
            disabled={!startDate}
            customInput={<CustomInput disabled={!startDate} />}
            dateFormat="dd/MM/yyyy"
          />
        </div>
      </div>
      {errorMessage && (
        <p className="text-red-500 text-xs mt-1">{errorMessage}</p>
      )}
    </div>
  );
};

export default RangeDateSearch;